 

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React from 'react';

export default function PackageDetailsWebScrapingTab({ webScrapingHistory }) {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatBytes = (bytes) => {
    if (!bytes) return 'N/A';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
      case 'completed':
        return 'success';
      case 'in transit':
      case 'processing':
      case 'out for delivery':
        return 'primary';
      case 'failed':
      case 'error':
        return 'error';
      case 'pending':
      case 'order processed':
        return 'warning';
      case 'departed facility':
        return 'info';
      default:
        return 'default';
    }
  };

  const getDotColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower?.includes('delivered')) return '#10B981';
    if (statusLower?.includes('transit') || statusLower?.includes('delivery'))
      return '#60A5FA';
    if (statusLower?.includes('departed') || statusLower?.includes('processed'))
      return '#F59E0B';
    return '#94A3B8';
  };

  // Custom Timeline Item Component
  const TimelineItem = ({ item, isLast }) => (
    <Box sx={{ display: 'flex', mb: 2 }}>
      {/* Timeline Dot and Line */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mr: 2
        }}
      >
        <FiberManualRecordIcon
          sx={{
            color: getDotColor(item.status),
            fontSize: '12px',
            mt: 1
          }}
        />
        {!isLast && (
          <Box
            sx={{
              width: '2px',
              height: '60px',
              backgroundColor: '#2D3748',
              mt: 1
            }}
          />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {item.status}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {item.location} • {formatDate(item.timestamp)}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {item.description}
        </Typography>
      </Box>
    </Box>
  );

  if (!webScrapingHistory || Object.keys(webScrapingHistory).length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No web scraping history available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Web scraping data will appear here when available from trackingRecord
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Tracking Record Details
      </Typography>

      {/* Main Information Card */}
      <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                <Chip
                  label={webScrapingHistory.status || 'Unknown Status'}
                  color={getStatusColor(webScrapingHistory.status)}
                  size="small"
                  sx={{ mr: 1 }}
                />
                Record ID: {webScrapingHistory.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last Scraped: {formatDate(webScrapingHistory.last_scraped_at)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'text.secondary' }}
              >
                Tracking Number
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {webScrapingHistory.tracking_number || 'N/A'}
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'text.secondary' }}
              >
                Shipping Company
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {webScrapingHistory.shipping_company || 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'text.secondary' }}
              >
                Created At
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {formatDate(webScrapingHistory.createdAt)}
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'text.secondary' }}
              >
                Last Updated
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {formatDate(webScrapingHistory.updatedAt)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tracking History */}
      {webScrapingHistory.tracking_history &&
        webScrapingHistory.tracking_history.length > 0 && (
          <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Tracking History
              </Typography>
              <Box sx={{ pl: 2 }}>
                {webScrapingHistory.tracking_history
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map((item, index, array) => (
                    <TimelineItem
                      key={index}
                      item={item}
                      isLast={index === array.length - 1}
                    />
                  ))}
              </Box>
            </CardContent>
          </Card>
        )}

      {/* Screenshots */}
      {webScrapingHistory.screenshots &&
        webScrapingHistory.screenshots.length > 0 && (
          <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Screenshots ({webScrapingHistory.screenshots.length})
              </Typography>
              <Grid container spacing={2}>
                {webScrapingHistory.screenshots.map((screenshot, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <img
                        src={screenshot}
                        alt={`Screenshot ${index + 1}`}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          objectFit: 'contain',
                          borderRadius: 4
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          display: 'none',
                          color: 'text.secondary',
                          fontStyle: 'italic'
                        }}
                      >
                        Image not available
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}
                      >
                        {screenshot.split('/').pop()}
                      </Typography>
                      <Link
                        href={screenshot}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        View Full Size
                      </Link>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

      {/* File Attachments */}
      {webScrapingHistory.file_attachments &&
        webScrapingHistory.file_attachments.length > 0 && (
          <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                File Attachments ({webScrapingHistory.file_attachments.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Filename</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Type</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Size</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {webScrapingHistory.file_attachments.map((file, index) => (
                      <TableRow key={index}>
                        <TableCell>{file.filename || 'Unnamed File'}</TableCell>
                        <TableCell>
                          <Chip
                            label={file.type || 'Unknown'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatBytes(file.size)}</TableCell>
                        <TableCell>
                          <Link
                            href={file.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: '0.875rem' }}
                          >
                            Download
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

      {/* Scraping Metadata */}
      {webScrapingHistory.scraping_metadata && (
        <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Scraping Metadata
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: 'text.secondary' }}
                    >
                      Source URL
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mb: 2, wordBreak: 'break-all' }}
                    >
                      <Link
                        href={webScrapingHistory.scraping_metadata.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {webScrapingHistory.scraping_metadata.source_url ||
                          'N/A'}
                      </Link>
                    </Typography>

                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: 'text.secondary' }}
                    >
                      Scraper Version
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {webScrapingHistory.scraping_metadata.scraper_version ||
                        'N/A'}
                    </Typography>

                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: 'text.secondary' }}
                    >
                      Response Time
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {webScrapingHistory.scraping_metadata.response_time_ms
                        ? `${webScrapingHistory.scraping_metadata.response_time_ms} ms`
                        : 'N/A'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: 'text.secondary' }}
                    >
                      Scraping Timestamp
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {formatDate(
                        webScrapingHistory.scraping_metadata.scraping_timestamp
                      )}
                    </Typography>

                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: 'text.secondary' }}
                    >
                      Captcha Encountered
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={
                          webScrapingHistory.scraping_metadata
                            .captcha_encountered
                            ? 'Yes'
                            : 'No'
                        }
                        color={
                          webScrapingHistory.scraping_metadata
                            .captcha_encountered
                            ? 'warning'
                            : 'success'
                        }
                        size="small"
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: 'text.secondary' }}
                    >
                      User Agent
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        backgroundColor: 'grey.100',
                        p: 1,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        wordBreak: 'break-all'
                      }}
                    >
                      {webScrapingHistory.scraping_metadata.user_agent || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Legacy Fields for backward compatibility */}
      {(webScrapingHistory.tracking_data ||
        webScrapingHistory.notes ||
        webScrapingHistory.error_message) && (
        <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Additional Information
            </Typography>

            {webScrapingHistory.tracking_data && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}
                >
                  Legacy Tracking Data
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    backgroundColor: 'grey.50',
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.75rem'
                  }}
                >
                  {typeof webScrapingHistory.tracking_data === 'object'
                    ? JSON.stringify(webScrapingHistory.tracking_data, null, 2)
                    : webScrapingHistory.tracking_data}
                </Typography>
              </Box>
            )}

            {(webScrapingHistory.notes || webScrapingHistory.error_message) && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}
                >
                  {webScrapingHistory.error_message ? 'Error Details' : 'Notes'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    backgroundColor: webScrapingHistory.error_message
                      ? 'error.light'
                      : 'grey.50',
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: webScrapingHistory.error_message
                      ? 'error.main'
                      : 'grey.200',
                    color: webScrapingHistory.error_message
                      ? 'error.contrastText'
                      : 'text.primary'
                  }}
                >
                  {webScrapingHistory.notes || webScrapingHistory.error_message}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
